import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { parse } from 'query-string';
import { useMutation } from '@apollo/client';
// Lib
import { initGoogleSDK } from 'lib/google-sdk';
// Api
import { GOOGLE_AUTH } from 'api/auth/mutations';
// Types
import { GoogleAuth, GoogleAuthVariables } from 'api/auth/types/GoogleAuth';
// Hooks
import { useDefferInitialization, useAppContext, useSocialAuth } from 'hooks';
// Ui
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';

const GoogleSdkWatcher = (): null | JSX.Element => {
  const { setGoogleSkdFailedStatus } = useAppContext();
  const { handleSocialAuthResponse, handleSocialAuthError } = useSocialAuth();
  const { asPath } = useRouter();
  const [isInitialized, setInitialization] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const ready = useDefferInitialization();

  const [googleAuth] = useMutation<GoogleAuth, GoogleAuthVariables>(
    GOOGLE_AUTH
  );

  const parsedUrl = parse(asPath);
  const code = (parsedUrl['/#code'] as string) || (parsedUrl['code'] as string);

  useEffect(() => {
    if (code) {
      setLoading(true);
    }

    if (code && isInitialized) {
      const login = async (): Promise<void> => {
        try {
          const { data } = await googleAuth({
            variables: {
              input: {
                code,
                // setting redirectUrl only for Local env, for others it retrieved from ENV on BE side
                ...(process.env.NODE_ENV === 'development' && {
                  redirectUrl: 'http://localhost:4000',
                }),
              },
            },
          });

          if (data?.googleAuth) {
            await handleSocialAuthResponse(data.googleAuth);
          }
        } catch (error) {
          handleSocialAuthError(error, 'Google');
        }

        setLoading(false);
      };

      login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, isInitialized]);

  useEffect(() => {
    const appId = process.env.NEXT_PUBLIC_GOOGLE_APP_ID;

    /**
     * Load scripts with a delay to improve page speed
     * performance & a metrics score
     */
    if (appId && ready) {
      initGoogleSDK({
        appId,
      })
        .then(() => {
          setInitialization(true);
        })
        .catch((error) => {
          setGoogleSkdFailedStatus(true);
          setLoading(false);
          console.log('initGoogleSDK', error);
        });
    }
  }, [ready, setGoogleSkdFailedStatus]);

  if (loading) {
    return <FourDotsLoader />;
  }

  return null;
};

export default GoogleSdkWatcher;
